import { Component, OnInit, Inject, Injector, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { Subject, lastValueFrom, takeUntil } from "rxjs";

import { AddressService } from "@core/services/address/address.service";
import { ToastrService } from "@app/core/services/notification/toastr.service";

import { BaseComponent } from "@app/core/components/base/base.component";
import { CustomerAddressResponse } from "@app/shared/models/api-response/customer-address";
import { PonchoUIModule } from "@poncho/ui";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";

type AddressField = {
    heading_text: string;
    placeholder_text: string;
    optional: boolean;
    default_text: string;
};

@Component({
    selector: "app-add-address",
    templateUrl: "./add-address.component.html",
    styleUrls: ["./add-address.component.scss"],
    standalone: true,
    imports: [MatDialogModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, PonchoUIModule]
})
export class AddAddressComponent extends BaseComponent implements OnInit, OnDestroy {

	addressType: number;
    addressForm: FormGroup<any>;
    addressFields: AddressField[];
    addingAddress: boolean = false;
    addressOptions: {
        value: number;
        label: string;
        disabled: boolean;
        icon: string;
    }[];

    dialogRef: MatDialogRef<any, any>;
    destroySubject$: Subject<void> = new Subject();
	polygon: any;

	address_locality: string;

    constructor(
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
        private injector: Injector,
        private toastrService: ToastrService,
        private addressService: AddressService,
        private bottomSheetRef: MatBottomSheetRef<AddAddressComponent>
    ) {
        super();
        if (!this.isMobile) {
            this.data = this.injector.get(MAT_DIALOG_DATA, null);
            this.dialogRef = this.injector.get(MatDialogRef, null);
        }
    }

    ngOnInit() {
        this.createFormFields();

		this.address_locality = this.data.formatted_address;
		if (this.polygon) {
			this.address_locality += ` (${this.polygon.getName()})`;
		}
    }

    createFormFields() {
        this.addressForm = new FormGroup({});

        const { address_input_form, address_line_hash, address_custom_name, address_type } = this.data;
		this.polygon = this.data.polygon;

		const addressFields: AddressField[] = address_input_form;
        // address_line_hash will be present incase of update address
        const addressLineHash = address_line_hash?.reduce((acc, curr) => ({ ...acc, ...curr }), {}) || {};
        // custom validator
        const doublePipeValidator = this.addressService.noDoublePipeValidator.bind(this.addressService);
        const whiteSpaceValidator = this.addressService.noWhitespaceValidator.bind(this.addressService);

        addressFields.forEach((field: AddressField, index: number) => {
            const validators = field.optional ? [doublePipeValidator] : [Validators.required, whiteSpaceValidator, doublePipeValidator];
            const defaultValue = addressLineHash[field.heading_text] ? addressLineHash[field.heading_text] : field.default_text;
            // using index, instead of heading_text, since heading_text can contains dot(.), and we can't use dot in control fields
            // ref: https://github.com/angular/angular/issues/25239
            this.addressForm.addControl(String(index), new FormControl(defaultValue, validators));
        });

        this.addressFields = addressFields;
        this.addressType = this.getAddressType(address_type);
        this.addressForm.addControl("addressType", new FormControl(this.addressType));
        this.addressForm.addControl("customName", new FormControl(address_custom_name || "", 
            this.addressType === 2 ? [Validators.required, whiteSpaceValidator, doublePipeValidator] : [doublePipeValidator]) // required only incase of other's address type
        );
        this.setAddressOptions(this.addressType);
    }

    getAddressType(address_type: string) {

        // edit address
        if (address_type) {
            const currentAddressType = this.addressService.addressTypes.indexOf(address_type.toLowerCase());
            if (currentAddressType > -1) {
                return currentAddressType;
            }
        }

        // new address
        const availableAddressTypes = this.addressService.getAvailableAddressTypes();

        if (availableAddressTypes.indexOf("home") > -1) {
            return 0;
        } else if (availableAddressTypes.indexOf("work") > -1) {
            return 1;
        } else {
            return 2;
        }
    }

    setAddressOptions(currentAddressType: number) {
        const availableAddressTypes = this.addressService.getAvailableAddressTypes();

        const showHomeAddressType = availableAddressTypes.includes("home") || currentAddressType === 0;
        const showWorkAddressType = availableAddressTypes.includes("work") || currentAddressType === 1;

        this.addressOptions = [
            { value: 0, label: "Home", disabled: !showHomeAddressType, icon: "home" },
            { value: 1, label: "Work", disabled: !showWorkAddressType, icon: "work" },
            { value: 2, label: "Other", disabled: false, icon: "location_on" } // others option always present
        ];
    }

    ngAfterViewInitBrowser(): void {
        this.addressForm.get("addressType").valueChanges
            .pipe(takeUntil(this.destroySubject$))
            .subscribe(() => {
                this.updateCustomNameValidation();
            });
    }

    updateCustomNameValidation() {
        const customNameControl = this.addressForm.get("customName");

        if (this.addressForm.get("addressType").value === 2) {
            customNameControl.setValidators([Validators.required]);
        } else {
            // clear and remove validation when address type is not others
            customNameControl.setValue("");
            customNameControl.clearValidators();
        }
        customNameControl.updateValueAndValidity();
    }

    setAddressType(value: number) {
        this.addressType = value;
        this.addressForm.get("addressType").setValue(value);
    }

    add(form: any) {
        if (form.invalid) {
            for (const key of Object.keys(this.addressForm.controls)) {
                if (this.addressForm.controls[key].invalid) {
                    const invalidControl = document.getElementById(key);
                    invalidControl.focus();
                    invalidControl.scrollIntoView();
                    break;
                }
            } 
            return;
        }
        
        let trimmedForm : any = {};
        Object.keys(form.value).forEach((key: any) => {
            if(typeof(this.addressForm.value[key]) === 'string') {
                trimmedForm[key] = this.addressForm.value[key].trim(); // trim whitespaces if any
            } else {
                trimmedForm[key] = this.addressForm.value[key]
            }
        })

        let addressLineHash = {};

        const { addressType, customName, ...restForm } = trimmedForm;
        const { isEditable, address_id, latitude, longitude, address_form_name } = this.data;
    
        // converting index to heading_text
        this.addressFields.forEach((fields: AddressField, index: number) => {
            addressLineHash[fields.heading_text] = restForm[index];
        });

        this.addingAddress = true;

        const action$ = isEditable
			? this.addressService.update(addressType, addressLineHash, customName, address_form_name, this.address_locality, address_id, { lat: latitude, lng: longitude }, this.polygon?.getId())
			: this.addressService.create(addressType, addressLineHash, customName, address_form_name, this.address_locality, { lat: latitude, lng: longitude }, this.polygon?.getId());
    
        lastValueFrom(action$)
            .then((data: CustomerAddressResponse) => {
                if (data.meta.code === 200) {
                    this.close(data);
                } else {
                    this.toastrService.openSnackBar(data.meta.message, "OKAY");
                }
            }).catch((e) => {
                console.log(e);
                this.toastrService.openSnackBar("Something went wrong. Please try again!", "OKAY");
            }).finally(() => {
                this.addingAddress = false;
            });
    }    

    close(data: any = null) {
        if (!this.isMobile) {
            this.dialogRef.close(data);
        } else {
            this.bottomSheetRef.dismiss(data);
        }
    }

    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }
}
